import React from 'react';
import { useTranslation } from 'react-i18next';
import './EditorJS.css';

const EditorToolbar = ({
    fontFamily,
    setFontFamily,
    fontSize,
    setFontSize,
    onInsertText,
    onChangeColor,
    isAiModifying,
    hasUnsavedChanges,
    saving,
    showChat,
    setShowChat,
    onSave,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    pendingAiChanges
}) => {
    const { t } = useTranslation('labels');

    return (
        <div className="editor-toolbar">
            {/* Font Family Dropdown */}
            <select
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                className="editor-toolbar-select editor-toolbar-font-family"
            >
                <option value="Calibri">Calibri</option>
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Verdana">Verdana</option>
                <option value="Georgia">Georgia</option>
            </select>

            {/* Font Size Dropdown */}
            <select
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                className="editor-toolbar-select editor-toolbar-font-size"
            >
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="14">14</option>
                <option value="16">16</option>
                <option value="18">18</option>
                <option value="20">20</option>
                <option value="24">24</option>
                <option value="28">28</option>
                <option value="32">32</option>
            </select>

            {/* Formatting Buttons */}
            <div className="editor-toolbar-section">
                <button
                    onClick={() => onInsertText('bold')}
                    className="editor-toolbar-button editor-toolbar-button-bold"
                    title="Grassetto"
                >
                    B
                </button>
                <button
                    onClick={() => onInsertText('italic')}
                    className="editor-toolbar-button editor-toolbar-button-italic"
                    title="Corsivo"
                >
                    I
                </button>
                <button
                    onClick={() => onInsertText('underline')}
                    className="editor-toolbar-button editor-toolbar-button-underline"
                    title="Sottolineato"
                >
                    U
                </button>
            </div>

            {/* Text Color */}
            <div className="editor-toolbar-color-section">
                <span className="editor-toolbar-color-label">Colore:</span>
                <input
                    type="color"
                    onChange={(e) => onChangeColor(e.target.value)}
                    className="editor-toolbar-color-picker"
                    title="Colore testo"
                />
            </div>

            {/* List Buttons */}
            <div className="editor-toolbar-section">
                <button
                    onClick={() => onInsertText('insertUnorderedList')}
                    className="editor-toolbar-button"
                    title="Elenco puntato"
                >
                    <i className="fas fa-list-ul"></i>
                </button>
                <button
                    onClick={() => onInsertText('insertOrderedList')}
                    className="editor-toolbar-button"
                    title="Elenco numerato"
                >
                    <i className="fas fa-list-ol"></i>
                </button>
            </div>

            {/* Alignment Buttons */}
            <div className="editor-toolbar-section">
                <button
                    onClick={() => onInsertText('justifyLeft')}
                    className="editor-toolbar-button"
                    title="Allinea a sinistra"
                >
                    <i className="fas fa-align-left"></i>
                </button>
                <button
                    onClick={() => onInsertText('justifyCenter')}
                    className="editor-toolbar-button"
                    title="Allinea al centro"
                >
                    <i className="fas fa-align-center"></i>
                </button>
                <button
                    onClick={() => onInsertText('justifyRight')}
                    className="editor-toolbar-button"
                    title="Allinea a destra"
                >
                    <i className="fas fa-align-right"></i>
                </button>
                <button
                    onClick={() => onInsertText('justifyFull')}
                    className="editor-toolbar-button"
                    title="Giustifica"
                >
                    <i className="fas fa-align-justify"></i>
                </button>
            </div>

            {/* Undo/Redo Buttons */}
            <div className="editor-toolbar-section">
                <button
                    onClick={onUndo}
                    className={`editor-toolbar-button ${!canUndo ? 'editor-toolbar-button-disabled' : ''}`}
                    disabled={!canUndo}
                    title="Annulla (Ctrl+Z)"
                >
                    <i className="fas fa-undo"></i>
                </button>
                <button
                    onClick={onRedo}
                    className={`editor-toolbar-button ${!canRedo ? 'editor-toolbar-button-disabled' : ''}`}
                    disabled={!canRedo}
                    title="Ripeti (Ctrl+Y)"
                >
                    <i className="fas fa-redo"></i>
                </button>
            </div>

            {/* Status Indicators & Buttons */}
            <div className="editor-toolbar-status">
                {/* AI Status Indicator */}
                {isAiModifying && (
                    <div className="d-flex align-items-center text-info editor-toolbar-ai-status">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        AI sta modificando...
                    </div>
                )}

                {/* AI Changes Indicator */}
                {pendingAiChanges && pendingAiChanges.size > 0 && (
                    <span
                        className="text-warning editor-toolbar-ai-changes"
                        title={`${pendingAiChanges.size} modifiche AI in attesa di conferma`}
                    >
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        {pendingAiChanges.size} AI
                    </span>
                )}

                {/* Unsaved Changes Indicator */}
                {hasUnsavedChanges && !saving && (
                    <span
                        className="text-warning editor-toolbar-unsaved"
                        title="Ci sono modifiche non salvate"
                    >
                        <i className="fas fa-circle me-1"></i>
                        Non salvato
                    </span>
                )}

                {/* AI Chat Toggle */}
                <a
                    className="text-primary fs-13 editor-toolbar-action-button"
                    onClick={() => setShowChat(prev => !prev)}
                    role="button"
                    title={'AI Features'}
                >
                    <i className={`fa-solid fa-robot fa-2x ${showChat ? 'text-primary' : ''}`}></i>
                </a>

                {/* Save Button */}
                <a
                    className={`text-primary fs-13 editor-toolbar-action-button ${saving ? 'editor-toolbar-save-disabled' : ''}`}
                    onClick={onSave}
                    role="button"
                    title={t('saveData', { ns: 'labels' })}
                >
                    <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} fa-2x`}></i>
                </a>
            </div>
        </div>
    );
};

export default EditorToolbar; 